import { useEffect, useMemo, useRef, useState } from 'react';
import { AppDataGrid, type AppColumn } from '../../../components/AppDataGrid';
import { AppDialog } from '../../../components/AppDialog';
import { CheckedCombo } from '../../../components/CheckedCombo';
import { UsDateInput } from '../../../components/UsDateInput';
import { fmtLocalDate } from '../../../utils/datetime';
import { apiErrorMessage, planInvoiceBatch } from './api';
import './invoicing.css';
import type {
  InvoiceBatchGroup, InvoiceBatchJob, InvoiceBatchPlan, InvoiceBatchRequest, InvoiceCreationMethod,
} from './types';

// ─────────────────────────────────────────────────────────────────────────────
// Invoice Queue ▸ New — "Invoice Creation Method" popup (owner design
// ALPACA_Invoice_Creation_Prototype_v13 + Design Guide, 2026-10-26).
//
//   Batch    — one Invoice ID per Job ID (flat preview, no client filter)
//   Multiple — one invoice per client, combining that client's ticked jobs
//   Group    — as Multiple, for the jobs RECEIVED inside the Invoice Period
//
// The grouping is the SERVER's (POST /invoicing/batch/plan, Application
// InvoiceBatchPlanner) — the popup only shows it, and the save re-plans from
// the same rules, so the preview and the invoices cannot disagree. OK hands the
// request back to the queue page, which confirms and creates.
// ─────────────────────────────────────────────────────────────────────────────

const METHODS: { value: InvoiceCreationMethod; name: string; title: string; hint: string }[] = [
  { value: 'Batch',    name: 'Batch',    title: 'One Invoice ID per Job ID',
    hint: 'Generate a separate invoice for every selected job.' },
  { value: 'Multiple', name: 'Multiple', title: 'Combine Job IDs for each client',
    hint: 'Combine selected jobs belonging to the same client into one invoice.' },
  { value: 'Group',    name: 'Group',    title: 'Group by Client and Invoice Period',
    hint: 'Generate one invoice for each client within the selected invoice period.' },
];

/** First and last day of the current month, ISO — the period the popup opens on. */
function thisMonth(): { from: string; to: string } {
  const now = new Date();
  const p = (n: number) => String(n).padStart(2, '0');
  const last = new Date(now.getFullYear(), now.getMonth() + 1, 0).getDate();
  const ym = `${now.getFullYear()}-${p(now.getMonth() + 1)}`;
  return { from: `${ym}-01`, to: `${ym}-${p(last)}` };
}

/** Unique labels for the Client box — two clients may share a name. */
function clientLabels(plan: InvoiceBatchPlan | null): Map<string, string | null> {
  const map = new Map<string, string | null>();
  const seen = new Map<string, number>();
  for (const c of plan?.clients ?? []) {
    const base = c.client?.trim() || '(No client)';
    const n = (seen.get(base) ?? 0) + 1;
    seen.set(base, n);
    map.set(n === 1 ? base : `${base} (${n})`, c.clientId);
  }
  return map;
}

type BatchRow = InvoiceBatchJob & { client: string | null };

export interface InvoiceCreationDialogProps {
  open: boolean;
  /** The jobs ticked in the Invoice Queue. */
  jobIds: string[];
  onClose: () => void;
  /** OK — the request and the plan it produced. */
  onConfirm: (request: InvoiceBatchRequest, plan: InvoiceBatchPlan) => void;
}

export function InvoiceCreationDialog({ open, jobIds, onClose, onConfirm }: InvoiceCreationDialogProps) {
  const [method, setMethod] = useState<InvoiceCreationMethod>('Batch');
  const [clients, setClients] = useState<string[] | null>(null);   // null = all
  const [period, setPeriod] = useState(thisMonth);
  const [plan, setPlan] = useState<InvoiceBatchPlan | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [expanded, setExpanded] = useState<Set<string | number>>(new Set());
  const [showPreview, setShowPreview] = useState(false);
  // The client list comes from the FIRST plan of this opening (every client of
  // the ticked jobs) and stays put while the user narrows it.
  const [labels, setLabels] = useState<Map<string, string | null>>(new Map());
  const seq = useRef(0);

  // Each opening starts on Batch, every client, this month (prototype open()).
  useEffect(() => {
    if (!open) return;
    setMethod('Batch');
    setClients(null);
    setPeriod(thisMonth());
    setPlan(null);
    setError(null);
    setExpanded(new Set());
    setShowPreview(false);
    setLabels(new Map());
  }, [open, jobIds]);

  const request = useMemo<InvoiceBatchRequest>(() => ({
    method,
    jobIds,
    clientIds: clients === null ? null
      : clients.map((l) => labels.get(l)).filter((id): id is string => !!id),
    periodFrom: method === 'Group' ? period.from || null : null,
    periodTo:   method === 'Group' ? period.to || null : null,
  }), [method, jobIds, clients, labels, period]);

  const periodInvalid = method === 'Group' && !!period.from && !!period.to && period.from > period.to;

  // Re-plan on every change (the server owns the grouping).
  useEffect(() => {
    if (!open || jobIds.length === 0) return;
    if (method === 'Group' && (!period.from || !period.to || periodInvalid)) { setPlan(null); setError(null); return; }
    const mine = ++seq.current;
    planInvoiceBatch(request)
      .then((p) => {
        if (mine !== seq.current) return;
        setPlan(p);
        setError(null);
        setShowPreview(false);
        setLabels((prev) => (prev.size > 0 ? prev : clientLabels(p)));
      })
      .catch((e) => {
        if (mine !== seq.current) return;
        setPlan(null);
        setError(apiErrorMessage(e) ?? 'The grouping could not be loaded.');
      });
  }, [open, jobIds, request, method, period, periodInvalid]);

  const batch = method === 'Batch';
  const periodText = method === 'Group' ? `${fmtLocalDate(period.from)} to ${fmtLocalDate(period.to)}` : 'All selected dates';
  // A plan answered for another method (the re-plan is still on its way) is
  // never drawn: Batch's groups repeat a client, and drawn in the client grid
  // they would leave a stale row behind.
  const current = plan && plan.method === method ? plan : null;
  const invoices = current?.invoices ?? [];
  const jobCount = current?.eligibleJobCount ?? 0;

  const batchRows = useMemo<BatchRow[]>(
    () => invoices.flatMap((g) => g.jobs.map((j) => ({ ...j, client: g.client }))),
    [invoices],
  );

  const batchColumns: AppColumn<BatchRow>[] = [
    { field: 'jobID',        header: 'Job ID',        width: 130,
      summary: () => `Total: ${batchRows.length} Job ID(s) · ${invoices.length} proposed Invoice ID(s)` },
    { field: 'client',       header: 'Client',        width: 240 },
    { field: 'projectID',    header: 'Project ID',    width: 150, value: (r) => r.projectID || '—' },
    { field: 'receivedDate', header: 'Date Received', width: 130, type: 'date' },
  ];

  const groupColumns: AppColumn<InvoiceBatchGroup>[] = [
    { field: 'client', header: 'Client', width: 260, summary: () => 'Total' },
    ...(method === 'Group'
      ? [{ field: 'invoiceCount', header: 'Invoice IDs', width: 110, align: 'right' as const,
           value: () => 1, summary: () => String(invoices.length) }]
      : []),
    { field: 'jobCount', header: 'Job IDs', width: 110, align: 'right' as const,
      value: (g) => g.jobs.length, summary: () => String(jobCount) },
    { field: 'period', header: 'Invoice Period', width: 230, value: () => periodText, summary: () => periodText },
  ];

  const jobColumns: AppColumn<InvoiceBatchJob>[] = [
    { field: 'jobID',        header: 'Job ID',        width: 140 },
    { field: 'projectID',    header: 'Project ID',    width: 160, value: (r) => r.projectID || '—' },
    { field: 'receivedDate', header: 'Date Received', width: 140, type: 'date' },
  ];

  const meta = current
    ? `${current.clientCount} client(s) · ${jobCount} eligible Job ID(s) · ${invoices.length} proposed Invoice ID(s)`
    : '';
  const note = current
    ? `${current.selectedCount} selected in Invoice Queue · ${jobCount} eligible Job ID(s) · ${current.clientCount} eligible client(s) · ${invoices.length} proposed Invoice ID(s)`
      + (current.excludedByPeriod ? ` · ${current.excludedByPeriod} client(s) excluded by Received Date period` : '')
    : '';

  const canOk = !!current && invoices.length > 0 && !periodInvalid;

  return (
    <AppDialog
      open={open}
      title="Invoicing"
      icon="ti-file-invoice"
      width={850}
      onClose={onClose}
      className="inv-create-dlg"
      footer={(
        <>
          <button type="button" className="ca-btn" disabled={!canOk} onClick={() => setShowPreview(true)}>Preview</button>
          <button type="button" className="ca-btn ca-btn-solid" disabled={!canOk}
            onClick={() => current && onConfirm(request, current)}>OK</button>
          <button type="button" className="ca-btn" onClick={onClose}>Cancel</button>
        </>
      )}
    >
      <div className="inv-cm">
        <div className="inv-cm-title">Invoice Creation Method</div>
        {METHODS.map((m) => (
          <label key={m.value} className="inv-cm-choice">
            <input type="radio" name="inv-cm-method" value={m.value} checked={method === m.value}
              onChange={() => setMethod(m.value)} data-unsaved-ignore />
            <span>
              <b>{m.name}</b> — {m.title}
              <small>{m.hint}</small>
            </span>
          </label>
        ))}

        {!batch && (
          <div className="inv-cm-section">
            <div className="inv-cm-row">
              <label>Client</label>
              <div className="inv-cm-combo" data-unsaved-ignore>
                <CheckedCombo
                  options={[...labels.keys()]}
                  values={clients ?? [...labels.keys()]}
                  onChange={(v) => setClients(v.length === labels.size ? null : v)}
                  placeholder="No Clients Selected"
                  unit="clients"
                  ariaLabel="Client"
                  loading={labels.size === 0}
                />
              </div>
            </div>
          </div>
        )}

        {method === 'Group' && (
          <div>
            <div className="inv-cm-row">
              <label>Invoice Period</label>
              <span className="inv-cm-range" data-unsaved-ignore>
                <span>From</span>
                <UsDateInput value={period.from} onChange={(v) => setPeriod((p) => ({ ...p, from: v }))} />
                <span>To</span>
                <UsDateInput value={period.to} onChange={(v) => setPeriod((p) => ({ ...p, to: v }))} />
              </span>
            </div>
            {periodInvalid && <div className="inv-cm-hint inv-cm-err">From Date must not be later than To Date.</div>}
            <div className="inv-cm-hint">
              Query selected Job IDs by Received Date within this period (both dates inclusive).
              Only clients with matching Job IDs appear below.
            </div>
          </div>
        )}

        <div className="inv-cm-panel">
          <div className="inv-cm-panel-title">Invoice Grouping Preview <span>{meta}</span></div>
          {batch ? (
            <AppDataGrid<BatchRow>
              rows={batchRows}
              columns={batchColumns}
              getRowId={(r) => r.oid}
              showFilters={false}
              hideFooter
              hideHeaderTools
              autoFocusFirstRow={false}
              className="inv-cm-grid"
              emptyText={error ?? 'No selected jobs.'}
            />
          ) : (
            <AppDataGrid<InvoiceBatchGroup>
              rows={invoices}
              columns={groupColumns}
              // One invoice per row: its first job is unique (a job is never on
              // two invoices), the client id is not (Batch repeats it).
              getRowId={(g) => g.jobs[0]?.oid ?? g.clientId ?? 'none'}
              showFilters={false}
              hideFooter
              hideHeaderTools
              autoFocusFirstRow={false}
              className="inv-cm-grid"
              emptyText={error ?? 'No Job IDs match the selected clients and Received Date period.'}
              rowDetail={{
                expandedIds: expanded,
                onExpandedChange: setExpanded,
                expandTitle: 'Show jobs',
                render: (g) => (
                  <div className="inv-cm-sub">
                    <AppDataGrid<InvoiceBatchJob>
                      rows={g.jobs}
                      columns={jobColumns}
                      getRowId={(j) => j.oid}
                      showFilters={false}
                      hideFooter
                      hideHeaderTools
                      autoFocusFirstRow={false}
                      className="inv-cm-subgrid"
                    />
                  </div>
                ),
              }}
            />
          )}
          <div className="inv-cm-note">{error ?? note}</div>
        </div>

        {showPreview && current && (
          <div className="inv-cm-preview">
            <AppDataGrid<InvoiceBatchGroup & { n: number }>
              rows={invoices.map((g, i) => ({ ...g, n: i + 1 }))}
              columns={[
                { field: 'n',      header: 'Proposed Invoice', width: 140, value: (g) => `New Invoice ${g.n}` },
                { field: 'client', header: 'Client',           width: 220 },
                { field: 'jobIds', header: 'Job IDs',          width: 320, value: (g) => g.jobs.map((j) => j.jobID).join(', ') },
                { field: 'jobs',   header: 'Jobs',             width: 70, align: 'right', value: (g) => g.jobs.length },
              ]}
              getRowId={(g) => g.n}
              showFilters={false}
              hideFooter
              hideHeaderTools
              autoFocusFirstRow={false}
              className="inv-cm-grid"
            />
          </div>
        )}
      </div>
    </AppDialog>
  );
}

export default InvoiceCreationDialog;
